import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Copy, Check } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface DeviceIdDisplayProps {
  deviceId: string;
  onClose: () => void;
}

export const DeviceIdDisplay = ({ deviceId, onClose }: DeviceIdDisplayProps) => {
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(deviceId);
      setCopied(true);
      toast({
        title: "Copied!",
        description: "Device ID has been copied to clipboard.",
      });
      
      // Reset the copied state after 2 seconds
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
      toast({
        title: "Error",
        description: "Failed to copy to clipboard. Please copy manually.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-4">
      <div className="text-center">
        <h3 className="text-lg font-semibold text-green-600 mb-2">
          ✅ Success!
        </h3>
        <p className="text-sm text-muted-foreground">
          Parent account, child profile, family group, and device have been created successfully.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="deviceId">Device ID</Label>
        <div className="flex space-x-2">
          <Input
            id="deviceId"
            value={deviceId}
            readOnly
            className="font-mono text-sm"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={handleCopyToClipboard}
            className="shrink-0"
          >
            {copied ? (
              <Check className="h-4 w-4" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Save this Device ID - it's needed to link the physical device to this account.
        </p>
      </div>

      <div className="flex justify-end">
        <Button onClick={onClose}>
          Close
        </Button>
      </div>
    </div>
  );
};